import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle2, Loader2, MessageCircle, ArrowRight, Home,
  AlertTriangle, Clock, ShoppingCart, RotateCw, XOctagon,
} from "lucide-react";
import { toast } from "sonner";
import BotanicalBackdrop, { LeafGlyph } from "@/components/BotanicalBackdrop";
import { formatINR, useCart } from "@/context/CartContext";
import { firePurchaseOnce } from "@/context/MetaPixelContext";
import usePageTitle from "@/hooks/usePageTitle";
import { startCashfreeCheckout, verifyCashfreePayment } from "@/lib/cashfree";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const loadRazorpay = () => new Promise((resolve) => {
  if (typeof window === "undefined") return resolve(false);
  if (window.Razorpay) return resolve(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

/**
 * Copy + visual variant per payment_status.
 * The page only shows the "Thank you / Order Received" success layout when
 * payment_status === 'paid'. All other states show a clearly different
 * "Payment Not Confirmed" surface — never labelled as a completed order.
 */
const STATUS_META = {
  paid: {
    tone: "success",
    pill: "bg-[#E1EFE4] text-[#2F5A38] border-[#B7D6BE]",
    icon: CheckCircle2,
    eyebrow: "Order Confirmed",
    heading: "Thank you",
    pillLabel: "Payment received",
    body: "Your ritual has begun. We'll confirm dispatch on WhatsApp and email — typically within 24 hours.",
  },
  pending: {
    tone: "pending",
    pill: "bg-[#FDECDB] text-[#B85E24] border-[#F3C89A]",
    icon: Clock,
    eyebrow: "Payment Pending",
    heading: "Complete your payment to confirm the order",
    pillLabel: "Payment pending",
    body: "This order is not confirmed yet. Retry the payment to lock it in — nothing has been dispatched or charged.",
  },
  failed: {
    tone: "failed",
    pill: "bg-[#F6DEDE] text-[#8A2323] border-[#E5B6B6]",
    icon: AlertTriangle,
    eyebrow: "Payment Failed",
    heading: "Your payment didn't go through",
    pillLabel: "Payment failed",
    body: "Don't worry — nothing has been charged. Your cart is intact and you can retry with a different method.",
  },
  cancelled: {
    tone: "cancelled",
    pill: "bg-[#F0EAE0] text-[#5A5140] border-[#D9CFBF]",
    icon: XOctagon,
    eyebrow: "Payment Cancelled",
    heading: "You cancelled the payment",
    pillLabel: "Payment cancelled",
    body: "Nothing has been charged. Your cart is still saved — retry whenever you're ready.",
  },
  whatsapp_handoff: {
    tone: "info",
    pill: "bg-[#DDEEF6] text-[#1F5E7A] border-[#B0D6E5]",
    icon: MessageCircle,
    eyebrow: "Awaiting Payment Link",
    heading: "Our team will send you a prepaid link",
    pillLabel: "WhatsApp handoff",
    body: "This order becomes confirmed only after you complete the prepaid link our team shares on WhatsApp.",
  },
};

const OrderConfirmationPage = () => {
  usePageTitle("Order Status");
  const { orderId } = useParams();
  const nav = useNavigate();
  const { clear } = useCart();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [verifyingCf, setVerifyingCf] = useState(searchParams.get("cf_return") === "1");

  const fetchOrder = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}`);
      setOrder(res.data);
      // Fire Meta Purchase strictly on verified paid orders — deduped by orderId.
      if (res.data && res.data.payment_status === "paid") {
        firePurchaseOnce(res.data.id, {
          value: (res.data.total || 0) / 100,
          currency: "INR",
          content_ids: (res.data.items || []).map((i) => i.slug),
          contents: (res.data.items || []).map((i) => ({ id: i.slug, quantity: i.qty, item_price: i.unit_price ? i.unit_price / 100 : undefined })),
          num_items: (res.data.items || []).reduce((s, i) => s + (i.qty || 0), 0),
          order_id: res.data.id,
        });
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load order");
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  // Returned from Cashfree's hosted page: the redirect is only a UX signal — verify server-to-server,
  // polling briefly while Cashfree still reports the payment as pending.
  useEffect(() => {
    if (searchParams.get("cf_return") !== "1") return;
    let cancelled = false;
    (async () => {
      let status = "pending";
      for (let i = 0; i < 6 && !cancelled; i++) {
        try {
          const res = await verifyCashfreePayment(orderId);
          status = res.payment_status;
        } catch (e) {
          status = "error";
          toast.error(e?.response?.data?.detail || "Could not verify the Cashfree payment yet.");
          break;
        }
        if (status !== "pending") break;
        await new Promise((r) => setTimeout(r, 2500));
      }
      if (cancelled) return;
      if (status === "paid") { clear(); toast.success("Payment received. Your order is confirmed."); }
      else if (status === "pending") toast.message("Payment is still being confirmed by Cashfree — this page will update once it lands.");
      else if (status === "failed") toast.error("Payment failed — your cart is intact, you can retry below.");
      else if (status === "cancelled") toast.message("Payment cancelled — your cart is intact.");
      await fetchOrder();
      setVerifyingCf(false);
      setSearchParams({}, { replace: true });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const retryPayment = async () => {
    if (!order) return;
    setRetrying(true);
    try {
      // Branch on the gateway the backend resolved for this order (saved active gateway at order time).
      if ((order.payment_gateway || order.payment_method) === "cashfree") {
        await startCashfreeCheckout(order); // redirects to Cashfree; returns to this page with ?cf_return=1
        return;
      }
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Payment SDK failed to load. Refresh and try again.");
      // If the order was previously marked cancelled/failed, reopen a fresh Razorpay
      // order for the SAME internal order id — no new HerbKranti order gets created.
      const rzp = await axios.post(`${API}/orders/${order.id}/razorpay/create`);
      const opts = {
        key: rzp.data.key_id,
        amount: rzp.data.amount,
        currency: rzp.data.currency,
        name: "HerbKranti",
        description: "HerbKranti Order",
        order_id: rzp.data.razorpay_order_id,
        theme: { color: "#D97535" },
        prefill: { name: order.customer_name, email: order.email, contact: order.phone },
        handler: async (resp) => {
          try {
            await axios.post(`${API}/orders/verify-payment`, {
              order_id: order.id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            firePurchaseOnce(order.id, {
              value: (order.total || 0) / 100,
              currency: "INR",
              content_ids: (order.items || []).map((i) => i.slug),
              contents: (order.items || []).map((i) => ({ id: i.slug, quantity: i.qty, item_price: i.unit_price ? i.unit_price / 100 : undefined })),
              num_items: (order.items || []).reduce((s, i) => s + (i.qty || 0), 0),
              order_id: order.id,
            });
            clear();
            await fetchOrder();
            toast.success("Payment received. Your order is confirmed.");
          } catch {
            try { await axios.post(`${API}/orders/${order.id}/cancel`); } catch {}
            toast.error("Payment could not be verified. Please try again.");
            await fetchOrder();
          } finally {
            setRetrying(false);
          }
        },
        modal: {
          ondismiss: async () => {
            try { await axios.post(`${API}/orders/${order.id}/cancel`); } catch {}
            toast.message("Payment cancelled — your cart is intact.");
            await fetchOrder();
            setRetrying(false);
          },
        },
      };
      const inst = new window.Razorpay(opts);
      inst.on && inst.on("payment.failed", async (evt) => {
        try { await axios.post(`${API}/orders/${order.id}/cancel`); } catch {}
        const reason = evt?.error?.description || "Payment failed";
        toast.error(`${reason} — please try again.`);
        await fetchOrder();
        setRetrying(false);
      });
      inst.open();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(detail || "Could not restart payment.");
      setRetrying(false);
    }
  };

  if (loading || verifyingCf) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3" data-testid={verifyingCf ? "order-verifying-cashfree" : "order-loading"}>
        <Loader2 size={22} className="animate-spin text-[var(--hk-orange)]" />
        {verifyingCf && <p className="text-[13px] text-[var(--hk-ink-2)]">Confirming your payment with Cashfree…</p>}
      </div>
    );
  }
  if (err || !order) {
    return (
      <section className="py-24 text-center">
        <div className="mx-auto max-w-lg px-6">
          <h1 className="serif text-[28px] text-[var(--hk-ink)]">Order not found</h1>
          <p className="mt-2 text-[14px] text-[var(--hk-ink-2)]">{err}</p>
          <Link to="/" className="hk-btn-primary inline-flex mt-6">Return Home <Home size={14} /></Link>
        </div>
      </section>
    );
  }

  const meta = STATUS_META[order.payment_status] || STATUS_META.pending;
  const Icon = meta.icon;
  const isPaid = order.payment_status === "paid";
  const canRetry = ["pending", "failed", "cancelled"].includes(order.payment_status)
    && ["razorpay", "cashfree"].includes(order.payment_gateway || order.payment_method);

  return (
    <>
      <section className="relative pt-16 pb-8 lg:pt-24 lg:pb-12 overflow-hidden">
        <BotanicalBackdrop variant="corners" />
        <div className="mx-auto max-w-3xl px-6 lg:px-10 text-center" data-testid={`order-status-${order.payment_status}`}>
          <div className={
            "w-14 h-14 rounded-full flex items-center justify-center mx-auto border " +
            (isPaid
              ? "bg-[var(--hk-warm-ivory)] border-[var(--hk-border)] text-[var(--hk-orange-deep)]"
              : (meta.tone === "failed" ? "bg-[#FBEAEA] border-[#E5B6B6] text-[#8A2323]"
                : meta.tone === "cancelled" ? "bg-[#F4EEE1] border-[#D9CFBF] text-[#5A5140]"
                : "bg-[#FDECDB] border-[#F3C89A] text-[#B85E24]"))
          }>
            <Icon size={22} />
          </div>
          <div className="label-eyebrow mt-5" data-testid="order-eyebrow">{meta.eyebrow}</div>
          <h1 className="serif mt-3 text-[32px] sm:text-[42px] leading-[1.1] text-[var(--hk-ink)]" data-testid="order-confirmation-title">
            {isPaid ? (
              <>Thank you, <span className="italic-accent text-[var(--hk-orange-deep)]">{order.customer_name.split(" ")[0]}.</span></>
            ) : (
              meta.heading
            )}
          </h1>
          <p className="mt-5 text-[15px] leading-[1.85] text-[var(--hk-ink-2)] max-w-xl mx-auto" data-testid="order-body-copy">
            {meta.body}
          </p>
          <div className="mt-6 inline-flex items-center gap-3 text-[13px] text-[var(--hk-ink-2)] px-4 py-2 rounded-full bg-white border border-[var(--hk-border)]">
            <span className="label-eyebrow !text-[10px]">Order Reference</span>
            <span className="serif text-[var(--hk-ink)] tracking-wider" data-testid="order-id">{order.id.slice(0, 8).toUpperCase()}</span>
          </div>

          {canRetry && (
            <div className="mt-6 flex flex-wrap gap-3 justify-center" data-testid="retry-payment-block">
              <button
                onClick={retryPayment}
                disabled={retrying}
                className="hk-btn-primary disabled:opacity-70"
                data-testid="retry-payment-btn"
              >
                {retrying ? <Loader2 size={14} className="animate-spin" /> : <RotateCw size={14} />}
                {retrying ? "Reopening…" : "Retry Payment"}
              </button>
              <Link to="/cart" className="hk-btn-ghost" data-testid="back-to-cart-btn">
                <ShoppingCart size={14} /> Back to Cart
              </Link>
            </div>
          )}
        </div>
      </section>

      <section className="relative py-8">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <div className="hk-card p-8" data-testid="order-summary">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="label-eyebrow">Order Summary</div>
              <span className={"px-3 py-1 rounded-full text-[11px] tracking-widest uppercase border " + meta.pill} data-testid="order-status-pill">
                {meta.pillLabel}
              </span>
            </div>
            <ul className="mt-5 space-y-4">
              {order.items.map((it) => (
                <li key={it.slug} className="flex items-center justify-between gap-4">
                  <div>
                    <div className="serif text-[15px] text-[var(--hk-ink)]">{it.name}</div>
                    <div className="text-[12px] text-[var(--hk-ink-3)] mt-0.5">Qty {it.qty} · Ships {it.shipped_units} {it.is_bogo && "(BUY 1 GET 1 FREE)"}</div>
                  </div>
                  <div className="serif text-[16px] text-[var(--hk-ink)]">{formatINR(it.unit_price * it.qty)}</div>
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-6 border-t border-[var(--hk-border)] space-y-2 text-[14px] text-[var(--hk-ink-2)]">
              <div className="flex justify-between"><span>Subtotal</span><span className="text-[var(--hk-ink)]">{formatINR(order.subtotal)}</span></div>
              <div className="flex justify-between"><span>Shipping</span><span className="text-[var(--hk-ink)]">Free</span></div>
              <div className="mt-2 pt-2 border-t border-[var(--hk-border)] flex justify-between">
                <span className="label-eyebrow">Total</span>
                <span className="serif text-[22px] text-[var(--hk-ink)]" data-testid="order-total">{formatINR(order.total)}</span>
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 mt-6">
            <div className="hk-card p-6">
              <div className="label-eyebrow">Delivery to</div>
              <div className="mt-3 text-[14px] text-[var(--hk-ink)] leading-[1.7]">
                <div className="serif text-[17px]">{order.customer_name}</div>
                <div className="mt-1 text-[var(--hk-ink-2)]">
                  {order.address_line1}<br />
                  {order.address_line2 && (<>{order.address_line2}<br /></>)}
                  {order.city}, {order.state} — {order.pincode}<br />
                  {order.phone} · {order.email}
                </div>
              </div>
            </div>
            <div className="hk-card p-6">
              <div className="label-eyebrow">Payment</div>
              <div className="mt-3 text-[14px] text-[var(--hk-ink)]">
                <div className="serif text-[17px] capitalize">{order.payment_method}</div>
                <div className="mt-1 text-[var(--hk-ink-2)] text-[13px] leading-[1.7]" data-testid="payment-status-copy">
                  {isPaid && "Payment received. We'll dispatch your order shortly."}
                  {order.payment_status === "pending" && "Payment is pending — please retry to confirm this order."}
                  {order.payment_status === "failed" && "The last payment attempt failed. Nothing was charged. Retry when ready."}
                  {order.payment_status === "cancelled" && "You cancelled the last payment attempt. Nothing was charged. Retry anytime."}
                  {order.payment_status === "whatsapp_handoff" && "Our team will share a prepaid payment link on WhatsApp. Order is confirmed only once payment is completed."}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <a href="https://wa.me/917249797972" target="_blank" rel="noreferrer" className="hk-btn-ghost !text-[#25D366] !border-[#25D366]/40 hover:!bg-[#25D366]/5" data-testid="order-whatsapp-btn">
              <MessageCircle size={14} /> WhatsApp us
            </a>
            <Link to="/products" className="hk-btn-primary" data-testid="order-continue-btn">
              Continue Shopping <ArrowRight size={16} />
            </Link>
          </div>
          {isPaid && (
            <div className="mt-8 text-center">
              <LeafGlyph className="mx-auto text-[var(--hk-gold-deep)] w-5 h-5" />
              <p className="mt-3 text-[12px] text-[var(--hk-ink-3)] tracking-wide max-w-md mx-auto">
                A considered thank-you for choosing HerbKranti. May this be the quiet start of a beautiful daily ritual.
              </p>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default OrderConfirmationPage;
